import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createReminder, updateReminder, operationalCustomers, workReminderPage, workReminderAes,
  customerReminderRecipients, retryReminderNotification, workReminderNotification,
  type WorkCustomer, type WorkReminder, type ReminderRecipient, type ReminderNotification,
} from '../api/client';
import { israelInput, israelUtc, workDate, workError } from '../lib/customerWork';
import { useAuth } from '../context/AuthContext';

const inputClass = 'w-full rounded-md border border-gray-300 p-2 text-sm';
const buttonClass = 'rounded-md border px-3 py-2 text-sm disabled:opacity-50';

export function CustomerSelector({ value, onChange }: {
  value?: WorkCustomer; onChange: (customer?: WorkCustomer) => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<WorkCustomer[]>([]);
  const [cursor, setCursor] = useState<string | null>();
  const [error, setError] = useState('');
  const sequence = useRef(0);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const current = ++sequence.current;
    setError(''); setRows([]); setCursor(null); setLoading(true);
    operationalCustomers({ q }).then(({ data }) => {
      if (sequence.current === current) { setRows(data.items); setCursor(data.next_cursor); }
    }).catch(e => { if (sequence.current === current) setError(workError(e, t('customerWork.loadError'))); })
      .finally(() => { if (sequence.current === current) setLoading(false); });
    return () => { sequence.current++; };
  }, [q, t]);
  return <div className="space-y-2">
    <input className={inputClass} aria-label={t('customerWork.searchCustomer')} value={q}
      onChange={e => setQ(e.target.value)} placeholder={t('customerWork.searchCustomer')} />
    <select className={inputClass} aria-label={t('customerWork.customer')} value={value?.customer_id ?? ''}
      onChange={e => onChange(rows.find(r => r.customer_id === e.target.value))}>
      <option value="">{t('customerWork.noCustomer')}</option>
      {value && !rows.some(r => r.customer_id === value.customer_id) &&
        <option value={value.customer_id}>{value.customer_name}</option>}
      {rows.map(r => <option key={`${r.provider}:${r.customer_id}`} value={r.customer_id}>{r.customer_name}</option>)}
    </select>
    {cursor && <button type="button" disabled={loading} className={buttonClass} onClick={async () => {
      const current = sequence.current;
      setLoading(true);
      try {
        const { data } = await operationalCustomers({ q, cursor });
        if (sequence.current === current) { setRows(old => [...old, ...data.items]); setCursor(data.next_cursor); }
      } catch (e) { if (sequence.current === current) setError(workError(e, t('customerWork.loadError'))); }
      finally { if (sequence.current === current) setLoading(false); }
    }}>{t('customerWork.loadMore')}</button>}
    {error && <p role="alert" className="text-red-700">{error}</p>}
  </div>;
}

export function CustomerReminderForm({ customer, activityId, initial, onSaved, onCancel }: {
  customer?: WorkCustomer; activityId?: string; initial?: WorkReminder;
  onSaved: (reminder: WorkReminder) => void; onCancel: () => void;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const workspace = useRef(user?.tenant_id);
  const workspaceChanged = workspace.current !== user?.tenant_id;
  const id = useId();
  const [selected, setSelected] = useState(customer);
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [due, setDue] = useState(initial?.effective_due_date ? israelInput(new Date(initial.effective_due_date)) : '');
  const [priority, setPriority] = useState<WorkReminder['priority']>(initial?.priority ?? 'medium');
  const [notify, setNotify] = useState(initial?.notify_assigned_aes ?? true);
  const [selectedIds, setSelectedIds] = useState<string[]>(initial?.notification_recipient_user_ids ?? []);
  const [recipients, setRecipients] = useState<ReminderRecipient[]>([]);
  const [recipientError, setRecipientError] = useState('');
  const [recipientLoading, setRecipientLoading] = useState(Boolean(customer));
  const [recipientReload, setRecipientReload] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const requestId = useRef(crypto.randomUUID());
  useEffect(() => {
    let active = true;
    setRecipients([]); setRecipientError('');
    if (!selected || workspaceChanged) { setRecipientLoading(false); return; }
    setRecipientLoading(true);
    customerReminderRecipients(selected.customer_id).then(({ data }) => {
      if (active) setRecipients(data);
    }).catch(e => { if (active) setRecipientError(workError(e, t('customerWork.loadError'))); })
      .finally(() => { if (active) setRecipientLoading(false); });
    return () => { active = false; };
  }, [selected?.customer_id, selected?.provider, user?.tenant_id, workspaceChanged, recipientReload, t]);
  const pickerRows = [...recipients, ...selectedIds.filter(uid => !recipients.some(r => r.user_id === uid))
    .map((uid): ReminderRecipient => ({ user_id: uid, name: uid, eligible: false, reason: 'inactive_membership' }))];
  const preview = pickerRows.filter(r => selectedIds.includes(r.user_id) || (notify && r.assigned_ae));
  if (workspaceChanged) return <div role="alert" className="rounded border p-4">
    {t('customerWork.workspaceChanged')}
    <button type="button" className={buttonClass} onClick={onCancel}>{t('common.cancel')}</button>
  </div>;
  return <form className="space-y-3 rounded-lg border bg-white p-4" onSubmit={async e => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      if (selected && (recipientLoading || recipientError)) throw new Error(t('customerWork.recipientsRequired'));
      const originalDue = initial?.effective_due_date ? israelInput(new Date(initial.effective_due_date)) : '';
      const changedDue = !initial || due !== originalDue;
      const iso = changedDue ? israelUtc(due, t('customerWork.invalidTime')) : undefined;
      if (iso && new Date(iso).getTime() <= Date.now()) throw new Error(t('customerWork.futureRequired'));
      const targetsChanged = initial && (Boolean(initial.notify_assigned_aes) !== Boolean(selected && notify)
        || [...(initial.notification_recipient_user_ids ?? [])].sort().join('\0') !== [...selectedIds].sort().join('\0'));
      if (targetsChanged && (notify || selectedIds.length) && new Date(iso ?? initial.effective_due_date ?? '').getTime() <= Date.now())
        throw new Error(t('customerWork.futureRequired'));
      const data = { title, description, due_date: changedDue ? iso : undefined, priority,
        notify_assigned_aes: Boolean(selected && notify), notification_recipient_user_ids: selected ? selectedIds : [] };
      const result = initial
        ? await updateReminder(initial.id, { ...data, version: initial.version })
        : await createReminder({ ...data, customer_id: selected?.customer_id, provider: selected?.provider,
          activity_id: activityId, request_id: requestId.current });
      onSaved(result.data as WorkReminder);
    } catch (e) { setError(workError(e, t('customerWork.saveError'))); }
    finally { setBusy(false); }
  }}>
    {!customer && !initial && <CustomerSelector value={selected} onChange={next => {
      setSelected(next); setSelectedIds([]); setRecipients([]); setRecipientLoading(Boolean(next));
    }} />}
    {selected && <p className="font-medium">{selected.customer_name}</p>}
    <label className="block" htmlFor={`${id}-title`}>{t('customerWork.title')}</label>
    <input id={`${id}-title`} className={inputClass} required maxLength={200} value={title} onChange={e => setTitle(e.target.value)} />
    <label className="block" htmlFor={`${id}-notes`}>{t('customerWork.notes')}</label>
    <textarea id={`${id}-notes`} className={inputClass} maxLength={4000} value={description} onChange={e => setDescription(e.target.value)} />
    <label className="block" htmlFor={`${id}-due`}>{t('customerWork.due')}</label>
    <input id={`${id}-due`} type="datetime-local" className={inputClass} required value={due} onChange={e => setDue(e.target.value)} />
    <label className="block" htmlFor={`${id}-priority`}>{t('customerWork.priority')}</label>
    <select id={`${id}-priority`} className={inputClass} value={priority}
      onChange={e => setPriority(e.target.value as WorkReminder['priority'])}>
      {(['low', 'medium', 'high', 'critical'] as const).map(p => <option key={p} value={p}>{t(`customerWork.${p}`)}</option>)}
    </select>
    {selected && <div className="space-y-2">
      <label className="flex gap-2"><input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} />{t('customerWork.notify')}</label>
      <fieldset className="space-y-2 rounded border p-3" disabled={recipientLoading}>
        <legend className="px-1 text-sm font-medium">{t('customerWork.selectRecipients')}</legend>
        {recipientLoading && <p>{t('common.loading')}</p>}
        {pickerRows.map(r => <label key={r.user_id} className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={selectedIds.includes(r.user_id)}
            disabled={!r.eligible && !selectedIds.includes(r.user_id)}
            onChange={e => setSelectedIds(old => e.target.checked ? [...old, r.user_id] : old.filter(uid => uid !== r.user_id))} />
          <span>{r.name}{r.role && ` (${t(`customerWork.roles.${r.role}`, { defaultValue: r.role })})`}
            {!r.eligible && <span className="block text-amber-700">{t(`customerWork.reasons.${r.reason}`, { defaultValue: r.reason })}</span>}
          </span>
        </label>)}
      </fieldset>
      {recipientError && <p role="alert" className="text-red-700">{recipientError}{' '}
        <button type="button" className="underline" onClick={() => setRecipientReload(n => n + 1)}>{t('customerWork.reload')}</button></p>}
      {(notify || selectedIds.length > 0) && <><p className="text-xs text-gray-600">{t('customerWork.templateGate')}</p>
        <p className="text-sm font-medium">{t('customerWork.recipientPreview')}</p>
        {notify && !recipients.some(r => r.assigned_ae) && !recipientLoading && !recipientError &&
          <p className="text-amber-700">{t('customerWork.noAe')}</p>}
        {preview.map(r => <p key={r.user_id} className={r.eligible ? 'text-gray-600' : 'text-amber-700'}>
          {r.name}{!r.eligible && ` — ${t(`customerWork.reasons.${r.reason}`, { defaultValue: r.reason })}`}
        </p>)}
      </>}
      {initial && !['pending', 'disabled', 'cancelled'].includes(initial.notification_status ?? 'disabled') &&
        <p className="text-amber-700">{t('customerWork.rescheduleWarning')}</p>}
    </div>}
    {error && <p role="alert" className="text-red-700">{error}</p>}
    <div className="flex gap-2">
      <button className={buttonClass} disabled={busy || Boolean(selected && (recipientLoading || recipientError))}>{t('common.save')}</button>
      <button className={buttonClass} type="button" disabled={busy} onClick={onCancel}>{t('common.cancel')}</button>
    </div>
  </form>;
}

function ReminderRow({ item, refresh, openCustomer }: {
  item: WorkReminder; refresh: () => void; openCustomer?: (customer: WorkCustomer) => void;
}) {
  const { t, i18n } = useTranslation();
  const [edit, setEdit] = useState(false);
  const [snooze, setSnooze] = useState('');
  const [showSnooze, setShowSnooze] = useState(false);
  const [detail, setDetail] = useState<ReminderNotification | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const enabled = Boolean(item.customer_id && (item.notify_assigned_aes || item.notification_recipient_user_ids?.length));
  const customer = item.customer_id ? {
    customer_id: item.customer_id, provider: item.provider, customer_name: item.customer_name ?? item.customer_id,
  } : undefined;
  const act = async (action: () => Promise<unknown>) => {
    setBusy(true); setError('');
    try { await action(); refresh(); setShowSnooze(false); }
    catch (e) { setError(workError(e, t('customerWork.saveError'))); }
    finally { setBusy(false); }
  };
  if (edit) return <CustomerReminderForm initial={item} customer={customer} onCancel={() => setEdit(false)}
    onSaved={() => { setEdit(false); refresh(); }} />;
  return <article className="space-y-2 rounded-lg border bg-white p-4" data-reminder-id={item.id}>
    <h3 className="font-semibold">{item.title}</h3>
    {customer && <button className="text-primary-700 underline" disabled={!openCustomer}
      onClick={() => openCustomer?.(customer)}>{customer.customer_name}</button>}
    {item.description && <p className="whitespace-pre-wrap text-sm">{item.description}</p>}
    <p className="text-sm">{workDate(item.effective_due_date, i18n.language)} · {t(`customerWork.taskStatus.${item.status}`, { defaultValue: item.status })}</p>
    <p className="text-xs">{(item.assigned_aes ?? []).map(a => a.name).join(', ')}</p>
    {enabled && <p className="text-sm">{t('customerWork.notification')}: {t(`customerWork.delivery.${item.notification_status}`, { defaultValue: item.notification_status })}</p>}
    {!!item.notification_recipient_user_ids?.length && <p className="text-xs">{t('customerWork.selectedCount', { count: item.notification_recipient_user_ids.length })}</p>}
    {item.notification_error && <p className="text-amber-700">{item.notification_error}</p>}
    {error && <p role="alert" className="text-red-700">{error}</p>}
    <div className="flex flex-wrap gap-2">
      {!['completed', 'dismissed'].includes(item.status) && <>
        <button className={buttonClass} disabled={busy} onClick={() => act(() => updateReminder(item.id, { status: 'completed', version: item.version }))}>{t('reminders.complete')}</button>
        <button className={buttonClass} disabled={busy} onClick={() => setEdit(true)}>{t('customerWork.edit')}</button>
        <button className={buttonClass} disabled={busy} onClick={() => setShowSnooze(!showSnooze)}>{t('common.snooze')}</button>
        <button className={buttonClass} disabled={busy} onClick={() => act(() => updateReminder(item.id, { status: 'dismissed', version: item.version }))}>{t('common.dismiss')}</button>
        {enabled && ['blocked', 'failed', 'partial'].includes(item.notification_status ?? '') &&
          <button className={buttonClass} disabled={busy} onClick={() => act(() => retryReminderNotification(item.id, item.version))}>{t('customerWork.retry')}</button>}
      </>}
      {enabled && <button className={buttonClass} disabled={busy} onClick={async () => {
        try { setDetail((await workReminderNotification(item.id)).data); }
        catch (e) { setError(workError(e, t('customerWork.loadError'))); }
      }}>{t('customerWork.deliveryDetails')}</button>}
    </div>
    {showSnooze && <div className="flex gap-2">
      <input type="datetime-local" className={inputClass} aria-label={t('customerWork.due')} value={snooze} onChange={e => setSnooze(e.target.value)} />
      <button className={buttonClass} disabled={busy || !snooze} onClick={() => act(() => updateReminder(item.id, {
        snoozed_until: israelUtc(snooze, t('customerWork.invalidTime')), version: item.version,
      }))}>{t('common.save')}</button>
    </div>}
    {detail && <div className="rounded bg-gray-50 p-3 text-sm">
      {detail.recipients.map(r => <p key={r.user_id}>{r.name}: {r.eligible ? t('customerWork.eligible') : t(`customerWork.reasons.${r.reason}`, { defaultValue: r.reason })}</p>)}
      {detail.attempts.map((a, index) => <p key={index}>{a.name}: {t(`customerWork.delivery.${a.status}`, { defaultValue: a.status })} {a.error}</p>)}
    </div>}
  </article>;
}

export default function CustomerReminderBoard({ customer, openCustomer, revision = 0 }: {
  customer?: WorkCustomer; openCustomer?: (customer: WorkCustomer) => void; revision?: number;
}) {
  const { t } = useTranslation();
  const [bucket, setBucket] = useState('');
  const [selected, setSelected] = useState(customer);
  const [ae, setAe] = useState('');
  const [aes, setAes] = useState<Array<{ user_id: string; name: string }>>([]);
  const [rows, setRows] = useState<WorkReminder[]>([]);
  const [cursor, setCursor] = useState<string | null>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [create, setCreate] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const sequence = useRef(0);
  const refresh = useCallback(() => setRefreshToken(n => n + 1), []);
  const params = { customer_id: selected?.customer_id, provider: selected?.provider, bucket: bucket || undefined, assigned_ae: ae || undefined };
  useEffect(() => {
    let active = true;
    workReminderAes().then(({ data }) => { if (active) setAes(data); })
      .catch(e => { if (active) setError(workError(e, t('customerWork.loadError'))); });
    return () => { active = false; };
  }, [t]);
  useEffect(() => {
    const current = ++sequence.current;
    setLoading(true); setError(''); setRows([]);
    workReminderPage(params).then(({ data }) => {
      if (sequence.current === current) { setRows(data.items); setCursor(data.next_cursor); }
    }).catch(e => { if (sequence.current === current) setError(workError(e, t('customerWork.loadError'))); })
      .finally(() => { if (sequence.current === current) setLoading(false); });
    return () => { sequence.current++; };
  }, [selected?.customer_id, selected?.provider, bucket, ae, refreshToken, revision, t]);
  return <section className="space-y-3">
    <div className="flex items-center justify-between gap-2"><h2 className="text-lg font-semibold">{t('nav.reminders')}</h2>
      <button className={buttonClass} onClick={() => setCreate(true)}>{t('reminders.createManual')}</button></div>
    {!customer && <CustomerSelector value={selected} onChange={setSelected} />}
    <div className="flex flex-wrap gap-2">
      {['', 'overdue', 'today', 'upcoming', 'completed'].map(tab =>
        <button key={tab} className={`${buttonClass} ${bucket === tab ? 'bg-primary-100' : ''}`} aria-pressed={bucket === tab}
          onClick={() => setBucket(tab)}>{tab ? t(`common.${tab}`) : t('customerWork.all')}</button>)}
      <select className="rounded border p-2 text-sm" aria-label={t('customerWork.aeFilter')} value={ae} onChange={e => setAe(e.target.value)}>
        <option value="">{t('customerWork.allAes')}</option>
        {aes.map(a => <option key={a.user_id} value={a.user_id}>{a.name}</option>)}
      </select>
    </div>
    {create && <CustomerReminderForm customer={selected} onCancel={() => setCreate(false)}
      onSaved={() => { setCreate(false); setBucket(''); refresh(); }} />}
    {error && <p role="alert" className="text-red-700">{error} <button className="underline" onClick={refresh}>{t('customerWork.reload')}</button></p>}
    {loading ? <p>{t('common.loading')}</p> : <>
      {!rows.length && !error && <p className="text-gray-500">{t('customerWork.noReminders')}</p>}
      {rows.map(item => <ReminderRow key={`${item.id}:${item.version}`} item={item} refresh={refresh} openCustomer={openCustomer} />)}
      {cursor && <button disabled={loading} className={buttonClass} onClick={async () => {
        const current = sequence.current;
        setLoading(true);
        try {
          const { data } = await workReminderPage({ ...params, cursor });
          if (sequence.current === current) { setRows(old => [...old, ...data.items]); setCursor(data.next_cursor); }
        } catch (e) { if (sequence.current === current) setError(workError(e, t('customerWork.loadError'))); }
        finally { if (sequence.current === current) setLoading(false); }
      }}>{t('customerWork.loadMore')}</button>}
    </>}
  </section>;
}
